import axios from 'axios';
import api from './axiosInstance';
import { API_BASE_URL } from 'config';

/**
 * Obtiene la lista paginada de demandantes excluidos
 * @param {number} page - Número de página (empieza en 1)
 * @param {number} pageSize - Cantidad de registros por página
 * @param {string} search - Término de búsqueda (opcional)
 * @param {boolean} ascending - Ordenamiento ascendente (default: true)
 * @returns {Promise<{total: number, data: Array}>}
 */

export async function getExcludedPlaintiffsPaged(
  page = 1,
  pageSize = 10,
  search = '',
  status = 'Activos',
  ascending = true,
  sortBy = null
) {
  try {
    const params = {
      page,
      pageSize,
      ...(search && { search }),
    };

    // status: 'Activos' | 'Inactivos' or boolean for isEnabled
    if (typeof status === 'boolean') {
      params.isEnabled = status;
    } else if (status && status !== 'Todos') {
      params.status = status;
    }

    if (ascending) {
      params.ascending = ascending === 'asc' ? true : false;
    }

    if (sortBy) {
      params.sortBy = sortBy;
    }

    const response = await api.get(`${API_BASE_URL}/ExcludedPlaintiffSaas`, {
      params,
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching excluded plaintiffs:', error);
    throw error;
  }
}

/**
 * Obtiene un demandante excluido por su ID
 * @param {number} id - ID del demandante excluido
 * @returns {Promise<Object>}
 */
export const getExcludedPlaintiffById = async (id) => {
  try {
    const response = await api.get(
      `${API_BASE_URL}/ExcludedPlaintiffSaas/${id}`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching excluded plaintiff ${id}:`, error);
    throw error;
  }
};

/**
 * Crea un nuevo demandante excluido
 * @param {Object} data - Datos del demandante excluido
 * @param {string} data.name - Nombre del demandante excluido (requerido)
 * @returns {Promise<Object>}
 */
export const createExcludedPlaintiff = async (data) => {
  try {
    if (!data.name || !data.name.trim()) {
      throw new Error('El nombre es requerido');
    }

    const payload = {
      name: data.name.trim(),
    };

    const response = await api.post(
      `${API_BASE_URL}/ExcludedPlaintiffSaas`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error('Error creating excluded plaintiff:', error);
    throw error;
  }
};

/**
 * Actualiza un demandante excluido existente
 * @param {number} id - ID del demandante excluido
 * @param {Object} data - Datos a actualizar
 * @param {string} data.name - Nombre del demandante excluido
 * @param {boolean} data.isEnabled - Estado activo/inactivo
 * @returns {Promise<Object>}
 */
export const updateExcludedPlaintiff = async (id, data) => {
  try {
    if (!data.name || !data.name.trim()) {
      throw new Error('El nombre es requerido');
    }

    const payload = {
      name: data.name.trim(),
      isEnabled: data.status === 'Activo', // Convertir status a isEnabled
    };

    const response = await api.put(
      `${API_BASE_URL}/ExcludedPlaintiffSaas/${id}`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error(`Error updating excluded plaintiff ${id}:`, error);
    throw error;
  }
};

/**
 * Elimina (desactiva) un demandante excluido
 * Soft delete - solo marca como inactivo, no elimina de BD
 * @param {number} id - ID del demandante excluido
 * @returns {Promise<Object>}
 */
export const deleteExcludedPlaintiff = async (id) => {
  try {
    const response = await api.delete(
      `${API_BASE_URL}/ExcludedPlaintiffSaas/${id}`
    );
    return response.data;
  } catch (error) {
    console.error(`Error deleting excluded plaintiff ${id}:`, error);
    throw error;
  }
};

/**
 * Servicio por defecto exportado con todos los métodos
 */
const excludedPlaintiffServices = {
  getExcludedPlaintiffsPaged,
  getExcludedPlaintiffById,
  createExcludedPlaintiff,
  updateExcludedPlaintiff,
  deleteExcludedPlaintiff,
};

export default excludedPlaintiffServices;
